import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import Portal from '@mui/material/Portal';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
// components
import Iconify from 'src/components/iconify';
//
import { useOnboarding } from './onboarding-context';

// Coach-mark/spotlight engine (§7: "coach-mark overlays, interactive
// walkthroughs that advance on click"). Deliberately framework-free (no
// driver.js/shepherd/react-joyride dependency — none was already installed,
// and this app's needs are modest: highlight one real DOM node per step,
// show a popover, advance on Next or on a click of the highlighted node
// itself). Steps target the `data-tour` attribute values already present on
// several hub-shell/tab surfaces.

function useStepTarget(selector) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!selector) {
      setRect(null);
      return undefined;
    }

    let frame;
    const measure = () => {
      const el = document.querySelector(`[data-tour="${selector}"]`);
      if (el) {
        setRect(el.getBoundingClientRect());
      } else {
        setRect(null);
      }
      frame = requestAnimationFrame(measure);
    };
    frame = requestAnimationFrame(measure);

    return () => cancelAnimationFrame(frame);
  }, [selector]);

  return rect;
}

export default function TourRunner() {
  const { activeTourKey, completeTour, dismissTour, tours } = useOnboarding();
  const tour = useMemo(() => tours.find((t) => t.key === activeTourKey) || null, [tours, activeTourKey]);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    setStepIndex(0);
  }, [activeTourKey]);

  const step = tour?.steps?.[stepIndex] || null;
  const rect = useStepTarget(step?.selector);

  useEffect(() => {
    if (!step) return undefined;
    const el = document.querySelector(`[data-tour="${step.selector}"]`);
    if (!el) return undefined;
    // "advance on click" — clicking the highlighted element itself moves the
    // tour forward, same as clicking Next.
    const onClick = () => handleNext();
    el.addEventListener('click', onClick, { once: true });
    return () => el.removeEventListener('click', onClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, stepIndex]);

  if (!tour || !step) return null;

  const isLast = stepIndex === tour.steps.length - 1;

  function handleNext() {
    if (isLast) {
      completeTour(tour.key);
    } else {
      setStepIndex((i) => i + 1);
    }
  }

  function handleBack() {
    setStepIndex((i) => Math.max(0, i - 1));
  }

  function handleSkip() {
    dismissTour(tour.key);
  }

  return (
    <Portal>
      {/* Dimmed backdrop with a cutout over the target, drawn via 4 box-shadow
          layers rather than an SVG mask — simpler for a single rectangular
          cutout and avoids a second render pass. */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: (theme) => theme.zIndex.modal + 10,
          pointerEvents: 'none',
        }}
      >
        {rect && (
          <Box
            sx={{
              position: 'fixed',
              top: rect.top - 6,
              left: rect.left - 6,
              width: rect.width + 12,
              height: rect.height + 12,
              borderRadius: 1,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
              border: (theme) => `2px solid ${theme.palette.primary.main}`,
              transition: 'all 0.2s ease',
            }}
          />
        )}

        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            pointerEvents: 'auto',
            p: 2.5,
            width: 320,
            top: rect ? Math.min(rect.bottom + 16, window.innerHeight - 220) : '40%',
            left: rect ? Math.min(Math.max(rect.left, 16), window.innerWidth - 336) : '50%',
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">{tour.label}</Typography>
            <IconButton size="small" onClick={handleSkip}>
              <Iconify icon="eva:close-fill" width={18} />
            </IconButton>
          </Stack>
          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>{step.title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{step.body}</Typography>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="caption" color="text.disabled">
              {stepIndex + 1} / {tour.steps.length}
            </Typography>
            <Stack direction="row" spacing={1}>
              {stepIndex > 0 && <Button size="small" onClick={handleBack}>Back</Button>}
              <Button size="small" variant="contained" onClick={handleNext}>
                {isLast ? 'Finish' : 'Next'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    </Portal>
  );
}

TourRunner.propTypes = {};

// A small pulsing dot over a `data-hotspot="<key>"` element, for features too
// small/new to deserve a full tour step (§7: "hotspots on new features").
// Renders nothing once the key has been seen.
export function Hotspot({ hotspotKey, children }) {
  const { state, seeHotspot } = useOnboarding();
  const seen = state?.seenHotspotKeys?.includes(hotspotKey);

  if (seen) return children;

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }} data-hotspot={hotspotKey} onClick={() => seeHotspot(hotspotKey)}>
      {children}
      <Box
        sx={{
          position: 'absolute',
          top: -3,
          right: -3,
          width: 10,
          height: 10,
          borderRadius: '50%',
          bgcolor: 'error.main',
          animation: 'onboarding-hotspot-pulse 1.6s ease-in-out infinite',
          '@keyframes onboarding-hotspot-pulse': {
            '0%': { boxShadow: '0 0 0 0 rgba(255,72,66,0.6)' },
            '70%': { boxShadow: '0 0 0 6px rgba(255,72,66,0)' },
            '100%': { boxShadow: '0 0 0 0 rgba(255,72,66,0)' },
          },
        }}
      />
    </Box>
  );
}

Hotspot.propTypes = {
  hotspotKey: PropTypes.string.isRequired,
  children: PropTypes.node,
};
